import { useEffect, useState } from 'react';
import type { Attachment } from '@/models/attachment';
import { PdfPreviewPane } from '@/features/report-detail';
import styles from './AttachmentViewer.module.css';

interface AttachmentViewerProps {
  attachment: Attachment | null;
}

function getFileExtension(url: string): string {
  const pathOnly = url.split('?')[0];
  const ext = pathOnly.split('.').pop()?.toLowerCase() ?? '';
  return ext;
}

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];

/** Fetch a URL as a Blob, re-fetching when the URL changes. */
function useFetchBlob(url: string | null): {
  blob: Blob | null;
  loading: boolean;
  error: unknown;
} {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (!url) {
      setBlob(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((b) => {
        if (!cancelled) {
          setBlob(b);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { blob, loading, error };
}

export default function AttachmentViewer({
  attachment,
}: AttachmentViewerProps) {
  const ext = attachment ? getFileExtension(attachment.file) : '';
  const isPdf = ext === 'pdf';

  const {
    blob: pdfBlob,
    loading: pdfLoading,
    error: pdfError,
  } = useFetchBlob(attachment && isPdf ? attachment.file : null);

  if (!attachment) {
    return (
      <div className={styles.empty}>
        <span>Select an attachment to view</span>
      </div>
    );
  }

  if (isPdf) {
    return (
      <PdfPreviewPane
        pdfBlob={pdfBlob}
        isLoading={pdfLoading}
        error={pdfError}
        showThumbnails
      />
    );
  }

  if (IMAGE_EXTENSIONS.includes(ext)) {
    return (
      <div className={styles.viewer}>
        <img
          src={attachment.file}
          alt={attachment.name}
          className={styles.image}
        />
      </div>
    );
  }

  return (
    <div className={styles.empty}>
      <span>
        This file type cannot be previewed.{' '}
        <a href={attachment.file} target="_blank" rel="noopener noreferrer">
          Download {attachment.name}
        </a>
      </span>
    </div>
  );
}
